import { FilterFilled } from "@ant-design/icons";
import { Checkbox, DatePicker, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { TaskStandAloneAlarm } from "shared/api";
import { TableButton } from "shared/ui";

interface UseTaskHubStandAloneSecondTableColumnsProps {
  isActiveFilter: boolean | undefined;
  setIsActiveFilter: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  refetch: () => void;
  onEdit: (record: TaskStandAloneAlarm) => void;
  onDelete: (id: number) => void;
  taskStandaloneFilter: number[];
  setTaskStandaloneFilter: React.Dispatch<React.SetStateAction<number[]>>;
  taskStandaloneOptions: { label: string; value: number }[];

  createdDateGreater: string | null;
  createdDateLesser: string | null;
  handleCreatedDateGreater: (date: any, dateString: string) => void;
  handleCreatedDateLesser: (date: any, dateString: string) => void;
  isCreatedFilterActive: boolean;

  updatedDateGreater: string | null;
  updatedDateLesser: string | null;
  handleUpdatedDateGreater: (date: any, dateString: string) => void;
  handleUpdatedDateLesser: (date: any, dateString: string) => void;
  isUpdatedFilterActive: boolean;

  maxValueGte: number | undefined;
  setMaxValueGte: React.Dispatch<React.SetStateAction<number | undefined>>;
  maxValueLte: number | undefined;
  setMaxValueLte: React.Dispatch<React.SetStateAction<number | undefined>>;
  minValueGte: number | undefined;
  setMinValueGte: React.Dispatch<React.SetStateAction<number | undefined>>;
  minValueLte: number | undefined;
  setMinValueLte: React.Dispatch<React.SetStateAction<number | undefined>>;

  triggeredDateGreater: string | null;
  triggeredDateLesser: string | null;
  handleTriggeredDateGreater: (date: any, dateString: string) => void;
  handleTriggeredDateLesser: (date: any, dateString: string) => void;
  isTriggeredFilterActive: boolean;

  commentsValue: string;
  handleCommentsValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useTaskHubStandAloneSecondTableColumns = ({
  isActiveFilter,
  setIsActiveFilter,
  refetch,
  onEdit,
  onDelete,
  setTaskStandaloneFilter,
  taskStandaloneFilter,
  taskStandaloneOptions,
  createdDateGreater,
  createdDateLesser,
  handleCreatedDateGreater,
  handleCreatedDateLesser,
  isCreatedFilterActive,
  handleUpdatedDateGreater,
  handleUpdatedDateLesser,
  isUpdatedFilterActive,
  updatedDateGreater,
  updatedDateLesser,
  maxValueGte,
  maxValueLte,
  minValueGte,
  minValueLte,
  setMaxValueGte,
  setMaxValueLte,
  setMinValueGte,
  setMinValueLte,
  handleTriggeredDateGreater,
  handleTriggeredDateLesser,
  isTriggeredFilterActive,
  triggeredDateGreater,
  triggeredDateLesser,
  commentsValue,
  handleCommentsValue,
}: UseTaskHubStandAloneSecondTableColumnsProps): ColumnsType<TaskStandAloneAlarm> => {
  return [
    {
      title: "Is Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (status: boolean) => (
        <span className={`${status} ? "limegreen" : "tomato"`}>
          {status ? "Active" : "No Active"}
        </span>
      ),
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[160px]">
          <div className="mb-1">Filter by Active</div>
          <select
            className="w-full border px-2 py-1 rounded"
            value={isActiveFilter ? "true" : "false"}
            onChange={(e) => {
              const v =
                e.target.value === "" ? undefined : e.target.value === "true";
              setIsActiveFilter(v);
              confirm();
              refetch();
            }}
          >
            <option value="">--</option>
            <option value="true">Active</option>
            <option value="false">No Active</option>
          </select>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isActiveFilter
              ? "!text-green-600 scale-125"
              : "!text-red-600 scale-125"
          }
        />
      ),
    },
    {
      title: "Task Standalone",
      dataIndex: ["task_standalone", "name"],
      key: "task_standalone",
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1">Filter by Task</div>
          <Checkbox.Group
            className="flex flex-col items-start space-y-1 max-h-[200px] overflow-y-auto"
            value={taskStandaloneFilter}
            onChange={(checkedValues) => {
              setTaskStandaloneFilter(checkedValues as number[]);
              refetch();
            }}
          >
            {taskStandaloneOptions.map((opt) => (
              <Checkbox key={opt.value} value={opt.value}>
                {opt.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            taskStandaloneFilter.length > 0
              ? "!text-blue-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "timestamp_created",
      key: "timestamp_created",
      render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm:ss"),
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">From</div>
          <DatePicker
            showTime
            value={createdDateGreater ? dayjs(createdDateGreater) : null}
            onChange={(val) =>
              handleCreatedDateGreater(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">To</div>
          <DatePicker
            showTime
            value={createdDateLesser ? dayjs(createdDateLesser) : null}
            onChange={(val) =>
              handleCreatedDateLesser(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isCreatedFilterActive ? "!text-yellow-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: "Updated",
      dataIndex: "timestamp_updated",
      key: "timestamp_updated",
      render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm:ss"),
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">Start From</div>
          <DatePicker
            showTime
            value={updatedDateGreater ? dayjs(updatedDateGreater) : null}
            onChange={(val) =>
              handleUpdatedDateGreater(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">Start To</div>
          <DatePicker
            showTime
            value={updatedDateLesser ? dayjs(updatedDateLesser) : null}
            onChange={(val) =>
              handleUpdatedDateLesser(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isUpdatedFilterActive ? "!text-yellow-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: "Min Value",
      dataIndex: "min_value",
      key: "min_value",
      render(_, record) {
        let icon = null;

        if (record.is_min_hard) {
          icon = (
            <Tooltip
              title={
                <div>
                  <div>Min Hard: {record.task_standalone.min_hard}</div>
                  <div>Min Soft: {record.task_standalone.min_soft}</div>
                  <div>Max Soft: {record.task_standalone.max_soft}</div>
                  <div>Max Hard: {record.task_standalone.max_hard}</div>
                </div>
              }
            >
              <img
                src="/red-standalone-icon.png"
                alt="Min Hard"
                className="w-[20px] h-[20px] ml-2"
              />
            </Tooltip>
          );
        } else if (record.is_min_soft) {
          icon = (
            <Tooltip
              title={
                <div>
                  <div>Min Hard: {record.task_standalone.min_hard}</div>
                  <div>Min Soft: {record.task_standalone.min_soft}</div>
                  <div>Max Soft: {record.task_standalone.max_soft}</div>
                  <div>Max Hard: {record.task_standalone.max_hard}</div>
                </div>
              }
            >
              <img
                src="/yellow-standalone-icon.png"
                alt="Min Soft"
                className="w-[20px] h-[20px] ml-2"
              />
            </Tooltip>
          );
        }

        return (
          <div className="flex items-center">
            <Tooltip title={record.min_value}>
              <span>{record.min_value.toFixed(2)}</span>
            </Tooltip>
            {icon}
          </div>
        );
      },
      filterDropdown: () => (
        <div className="p-2 w-[200px]">
          <div className="mb-1">Filter Min Value</div>
          <input
            type="number"
            placeholder=">= ..."
            className="w-full border px-2 py-1 rounded mb-2"
            value={minValueGte ?? ""}
            onChange={(e) =>
              setMinValueGte(
                e.target.value ? Number(e.target.value) : undefined
              )
            }
          />
          <input
            type="number"
            placeholder="<= ..."
            className="w-full border px-2 py-1 rounded"
            value={minValueLte ?? ""}
            onChange={(e) =>
              setMinValueLte(
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            onBlur={() => {
              refetch();
            }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            minValueGte !== undefined || minValueLte !== undefined
              ? "!text-purple-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: "Max Value",
      dataIndex: "max_value",
      key: "max_value",
      render(_, record) {
        let icon = null;

        if (record.is_max_hard) {
          icon = (
            <Tooltip
              title={
                <div>
                  <div>Min Hard: {record.task_standalone.min_hard}</div>
                  <div>Min Soft: {record.task_standalone.min_soft}</div>
                  <div>Max Soft: {record.task_standalone.max_soft}</div>
                  <div>Max Hard: {record.task_standalone.max_hard}</div>
                </div>
              }
            >
              <img
                src="/red-standalone-icon.png"
                alt="Min Hard"
                className="w-[25px] h-[25px] ml-2"
              />
            </Tooltip>
          );
        } else if (record.is_max_soft) {
          icon = (
            <Tooltip
              title={
                <div>
                  <div>Min Hard: {record.task_standalone.min_hard}</div>
                  <div>Min Soft: {record.task_standalone.min_soft}</div>
                  <div>Max Soft: {record.task_standalone.max_soft}</div>
                  <div>Max Hard: {record.task_standalone.max_hard}</div>
                </div>
              }
            >
              <img
                src="/yellow-standalone-icon.png"
                alt="Min Soft"
                className="w-[25px] h-[25px] ml-2"
              />
            </Tooltip>
          );
        }

        return (
          <div className="flex items-center">
            <Tooltip title={record.max_value}>
              {record.max_value.toFixed(2)}
            </Tooltip>
            <span>{icon}</span>
          </div>
        );
      },
      filterDropdown: () => (
        <div className="p-2 w-[200px]">
          <div className="mb-1">Filter Max Value</div>
          <input
            type="number"
            placeholder=">= ..."
            className="w-full border px-2 py-1 rounded mb-2"
            value={maxValueGte ?? ""}
            onChange={(e) =>
              setMaxValueGte(
                e.target.value ? Number(e.target.value) : undefined
              )
            }
          />
          <input
            type="number"
            placeholder="<= ..."
            className="w-full border px-2 py-1 rounded"
            value={maxValueLte ?? ""}
            onChange={(e) =>
              setMaxValueLte(
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            onBlur={() => {
              refetch();
            }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            maxValueGte !== undefined || maxValueLte !== undefined
              ? "!text-purple-600 scale-125"
              : undefined
          }
        />
      ),
    },

    {
      title: "Triggered",
      dataIndex: "timestamp_triggered",
      key: "timestamp_triggered",
      render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm:ss"),
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">From</div>
          <DatePicker
            showTime
            value={triggeredDateGreater ? dayjs(triggeredDateGreater) : null}
            onChange={(val) =>
              handleTriggeredDateGreater(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">To</div>
          <DatePicker
            showTime
            value={triggeredDateLesser ? dayjs(triggeredDateLesser) : null}
            onChange={(val) =>
              handleTriggeredDateLesser(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isTriggeredFilterActive ? "!text-yellow-600 scale-125" : undefined
          }
        />
      ),
    },

    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1">Filter by Comments</div>
          <input
            type="text"
            className="w-full border px-2 py-1 rounded"
            value={commentsValue}
            onChange={(e) => {
              handleCommentsValue(e);
            }}
            placeholder="Search comments..."
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={commentsValue ? "!text-blue-600 scale-125" : undefined}
        />
      ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (record: TaskStandAloneAlarm) => (
        <div
          className="flex gap-2"
          onClick={(e) => e.stopPropagation()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
        >
          <TableButton type="edit" onClick={() => onEdit(record)} />
          <TableButton type="delete" onClick={() => onDelete(record.id)} />
        </div>
      ),
    },
  ];
};
